/**
 * Proposal Details for the Nexus application.
 *
 * <i>Copyright (c) 2020 Energi Cryptocurrency - https://energi.world</i><br>
 * Proprietary License
 *
 *
 * @module components/subpages/treasury-budget/proposal-details.jsx
 * @class ProposalDetails
 * @since 0.3.8
*/
import React, {useEffect, useState} from 'react';
import Web3 from 'web3';
import axios from 'axios';
import {useStore} from 'effector-react';
import {IconButton} from '@rmwc/icon-button';
import {Button} from '@rmwc/button';
import {Ripple} from '@rmwc/ripple';
import {Icon} from '@rmwc/icon';
import {saveAs} from 'node-safe-filesaver';
import Footer from '../../Footer';
import treasury from '../../../lib/smart-contracts/treasury';
import translations from '../../../lib/translations';
import mockGovernance from '../../../lib/smart-contracts/mock-governance';
import menuState from '../../../globals/menu';
import demoState from '../../../globals/demo-usage';
import metamaskStore from '../../../globals/metamask';
import imgWithdraw from '../../../images/withdraw-active.svg';
import {proposalStructure, textToUUID} from '../../../lib/helpers/uuid';
import logo from '../../../images/mew-logo.svg';
import fileImg from '../../../images/file.svg';
import {discordReroute} from '../../../lib/helpers/network-providers';

const ConfirmProposal = ({amountField, payerField, periodField, proposalInfoField, onReset, onSetErrorMessage, setupTransaction, web3, ...props}) => {
    let mew, download, budgetPropose,
        classFiledownloadStep = 'step';

    const translate = translations.init(props),
        correctNetwork = useStore(metamaskStore.$correctNetwork),
        metamaskAddress = useStore(metamaskStore.$address),
        isDemo = demoState.$demo.getState(),
        [step, setStep] = useState(1),
        [proposalData, setProposalData] = useState({}),
        signWithMEW = (!correctNetwork || !metamaskAddress);

    useEffect(() => {
        menuState.on.hide();
        window.scrollTo(0, 0);

        return () => {
            menuState.on.open();
        };
    }, []);

    const downloadProposal = () => {
        let blob;
        const timestamp = Date.now(),
            weiAmount = Web3.utils.toWei(amountField, 'ether'),
            uuid = textToUUID(weiAmount, periodField, timestamp, payerField, proposalInfoField);

        setProposalData({weiAmount, uuid});
        blob = new Blob(['UUID: ' + uuid, '\n', '\n', proposalStructure(weiAmount, periodField, timestamp, payerField, proposalInfoField)], {type: 'text/plain;charset=utf-8'});

        saveAs(blob, 'UUID.txt');
        setStep(2);
    };

    const sendToDiscord = async (uuid, proposalInfoField) => {
        try {
            await axios.post(`${discordReroute}`, {
                content: {
                    uuid,
                    description: proposalInfoField
                }
            });
        }
        catch (err) {
            console.error(err);
        }
    };

    const handleCreateProposal = async e => {
        e.preventDefault();
        try {
            if (isDemo) {
                ({budgetPropose} = mockGovernance);
            }
            else {
                ({budgetPropose} = treasury(web3));
            }
            await sendToDiscord(proposalData.uuid, proposalInfoField);
            await setupTransaction(budgetPropose.bind(
                null,
                proposalData.weiAmount,
                proposalData.uuid,
                periodField
            ));
            onReset();
        }
        catch (err) {
            console.error(err);
            onSetErrorMessage(translate.t('SOMETHING_WENT_WRONG_PROPOSAL', true) + '.');
        }
    };

    if (step === 1) {
        download = (
            <>
                <div>{translate.t('DOWNLOAD_PROPOSAL', true)}</div>
                <div className="proposal-img-cont">
                    <img src={fileImg} />
                    <div>{translate.t('PROPOSAL', true) + ': UUID.txt'}</div>
                </div>
                <Button
                    className="btn"
                    icon={imgWithdraw}
                    label={translate.t('DOWNLOAD')}
                    onClick={downloadProposal}
                    raised
                    ripple={false} />
            </>
        );
    }
    else {
        if (signWithMEW) {
            mew = (
                <a className="anchor-no-underline" onClick={handleCreateProposal}>
                    <Ripple className="ripple-wrapper">
                        <div className="energi-mew-btn">
                            <div className="logo-title">
                                <img alt="energi logo" src={logo} />
                            </div>
                            <div className="logo-bottom">
                                <span>{translate.t('GOTO_ENERGI_WALLET', true)}</span>
                                <Icon icon="call_made" />
                            </div>
                        </div>
                    </Ripple>
                </a>
            );
        }
        else {
            mew = (
                <Button
                    className="btn"
                    label={translate.t('SIGN_PROPOSAL')}
                    onClick={handleCreateProposal}
                    raised
                    ripple={false} />
            );
        }
        classFiledownloadStep += ' done';
    }

    return (
        <div className="treasury-budget pure-u subpage no-menu">
            <section className="confirm-proposal">
                <div className="top-bar">
                    <table>
                        <tbody>
                            <tr>
                                <td><IconButton
                                    icon="arrow_back"
                                    onClick={() => props.setState(0)}
                                    style={{color: 'rgba(0, 0, 0, 0.54)'}}/>
                                </td>
                                <td><span className="top-bar-text">{translate.t('CONFIRM_YOUR_PROPOSAL', true)}</span></td>
                            </tr>
                        </tbody>
                    </table>
                </div>
                <div className="step-area">
                    <div className={classFiledownloadStep}>{translate.t('STEP1', true)}. - {translate.t('FILE_DOWNLOAD', true)} </div>
                    {download}
                    <div className={`${step === 1 ? 'deactivate ' : ''}step sign-step`}>{translate.t('STEP2', true)}. - {translate.t('SIGN_PROPOSAL', true)} </div>
                    {mew}
                </div>
            </section>
            <Footer {...props} />
        </div>
    );
};

export default ConfirmProposal;
